import { BaseDialog, BaseInput, BaseRadioGroup } from '@/shared/components'
import { BaseCopyButton } from '@/shared/components/BaseCopyButton/BaseCopyButton'
import { formatUSDT } from '@/shared/lib/currency/formatUSDT'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { BalanceType } from '@/entities/balances'
import { CryptoInvoice, useTransferBalance } from '@/entities/transferBalance'
import { useApiQuery } from '@/api/main/query'
import { useToast } from '@/shared/hooks'
import QRCode from 'qrcode'
import { Skeleton } from '@/shared/components/shadcn'
import { DepositInfoPanel } from '@/features/sidebar/dialogs/DepositInfoPanel.tsx'

type TopupDialogProps = {
  isVisible: boolean
  onClose: () => void
  balanceRewards: number
}

type TransferForm = {
  transferAmount: number
}

export const TopupDialog = ({ isVisible, onClose, balanceRewards }: TopupDialogProps) => {
  const [selectedOption, setSelectedOption] = useState<'wallet_trc20' | 'profit_balance'>(
    'wallet_trc20'
  )
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const { addToast } = useToast()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TransferForm>({
    defaultValues: {
      transferAmount: 0,
    },
  })

  const { handleTransferBalance, isPendingTransfer } = useTransferBalance(() => {
    onCLoseHandler()
  })

  const {
    data: invioceData,
    error: invoiceError,
    isLoading: isInvioceLoading,
  } = useApiQuery<CryptoInvoice>(['crypro-invoice'], '/v1/crypto/deposits/invoice')

  useEffect(() => {
    if (invioceData?.address) {
      const qrPayload = `tron:${invioceData.address}`
      QRCode.toDataURL(qrPayload)
        .then((url) => setQrCodeUrl(url))
        .catch((err) => {
          addToast('Ошибка!', `Ошибка при генерации QR:  ${err.message}`, 'error')
        })
    }
  }, [invioceData])

  useEffect(() => {
    if (invoiceError) {
      addToast('Ошибка!', 'Не удалось загрузить адрес для пополнения', 'error')
    }
  }, [invoiceError])

  const profitBalance = formatUSDT(balanceRewards)

  const onCLoseHandler = () => {
    onClose()
    setSelectedOption('wallet_trc20')
    reset()
  }

  const onSubmit = (data: TransferForm) => {
    if (selectedOption === 'profit_balance') {
      // Перевод с User баланса на Deposit баланс
      handleTransferBalance({
        balanceTypeFrom: BalanceType.User,
        balanceTypeTo: BalanceType.Deposit,
        amount: data.transferAmount,
      })
    } else if (selectedOption === 'wallet_trc20') {
      // Логика для wallet_trc20 (если нужна)
      onCLoseHandler()
    }
  }

  const trcContent = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-[10px]">
        <BaseInput
          className="w-full"
          value={invioceData?.address ?? ''}
          variant="secondary"
          readOnly
        />
        <BaseCopyButton color="primary" text={invioceData?.address ?? ''} />
      </div>
      <div className="flex flex-col justify-center items-center gap-2">
        <p className="text-sm">QR-код для пополнения</p>
        {isInvioceLoading ? (
          <Skeleton className="w-30 h-30" />
        ) : qrCodeUrl ? (
          <img src={qrCodeUrl} alt="qr code" className="w-30 h-30" />
        ) : (
          <div className="flex justify-center items-center w-30 h-30 text-text-secondary">
            QR не доступен.
          </div>
        )}
      </div>
    </div>
  )

  const profitContent = (
    <div className="flex flex-col gap-y-4 text-sm">
      <BaseInput label="Баланс вознаграждения:" value={profitBalance} disabled variant="default" />
      <BaseInput
        label="Введите сумму перевода:"
        placeholder="0.00"
        rightText="USDT"
        variant="default"
        type="number"
        error={errors.transferAmount?.message}
        {...register('transferAmount', {
          required: 'Обязательное поле',
          min: {
            value: 0.01,
            message: 'Сумма должна быть больше 0',
          },
          max: {
            value: balanceRewards,
            message: `Сумма не может превышать ${formatUSDT(balanceRewards)}`,
          },
          validate: {
            positive: (value) => value > 0 || 'Сумма должна быть положительной',
            notExceedBalance: (value) =>
              value <= balanceRewards ||
              `Недостаточно средств. Доступно: ${formatUSDT(balanceRewards)}`,
          },
        })}
      />
    </div>
  )

  return (
    <BaseDialog
      isVisible={isVisible}
      onClose={onCLoseHandler}
      onAction={selectedOption==='wallet_trc20'?onCLoseHandler:handleSubmit(onSubmit)}
      title="Пополнение депозита"
      actionButtonName={selectedOption === 'profit_balance' ? 'Создать депозит' : 'Готово'}
      closeButtonName="Отмена"
      closeButton
      isLoading={isPendingTransfer}
    >
      <BaseRadioGroup<'wallet_trc20' | 'profit_balance'>
        value={selectedOption}
        onChange={setSelectedOption}
        options={[
          {
            name: (
              <span>
                На кошелек для пополнения{' '}
                <span className="text-primary dark:text-primary-dark">TRC-20</span>
              </span>
            ),
            value: 'wallet_trc20',
            content: trcContent,
          },
          {
            name: 'Пополнить с баланса вознаграждения',
            value: 'profit_balance',
            content: profitContent,
          },
        ]}
      />

      <DepositInfoPanel />

    </BaseDialog>
  )
}
